import React from "react";

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Typewriter headline that keeps the real text in the DOM from the first paint.
 *
 * Three layers, each doing one job:
 *   1. an invisible copy of the full string, which reserves the final layout
 *      box — on a narrow screen the headline wraps to two lines partway
 *      through typing, and without this the whole page below it jumps;
 *   2. a screen-reader-only copy, so assistive tech and crawlers always get a
 *      complete heading (`react-typist`, which this replaces, mounted nothing
 *      until typing finished);
 *   3. the animated copy, hidden from assistive tech.
 *
 * @param segments [{ text, className }] — lets parts of the line be styled.
 */
export default function TypeLine({ segments, speed = 85, className = "" }) {
    const full = segments.map(s => s.text).join("");
    const [count, setCount] = React.useState(() =>
        prefersReducedMotion() ? full.length : 0
    );

    React.useEffect(() => {
        if (count >= full.length) return undefined;
        const id = setTimeout(() => setCount(c => c + 1), speed);
        return () => clearTimeout(id);
    }, [count, full.length, speed]);

    const done = count >= full.length;

    const caret = extra => (
        <span
            className={`ml-1 font-light text-accent ${
                extra ? "animate-caret" : ""
            }`}
        >
            |
        </span>
    );

    // Distribute the revealed character budget across the styled segments.
    let remaining = count;
    const visible = segments.map(segment => {
        const shown = segment.text.slice(0, Math.max(remaining, 0));
        remaining -= segment.text.length;
        return shown;
    });

    return (
        <div className={`relative ${className}`}>
            {/* Includes the caret so the reserved box matches the final line
                exactly — otherwise the caret can wrap past the measured height. */}
            <span aria-hidden="true" className="invisible">
                {full}
                {caret(false)}
            </span>

            <h1 className="absolute inset-0">
                <span className="sr-only">{full}</span>
                <span aria-hidden="true">
                    {segments.map((segment, i) => (
                        <span key={i} className={segment.className}>
                            {visible[i]}
                        </span>
                    ))}
                    {caret(done)}
                </span>
            </h1>
        </div>
    );
}
